import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'

const getOrCreateLegendList = (chart, id) => {
    const legendContainer = document.getElementById(id)
    let listContainer = legendContainer.querySelector('ul')

    if (!listContainer) {
        listContainer = document.createElement('ul')
        listContainer.style.display = 'flex'
        listContainer.style.flexDirection = 'column'
        listContainer.style.gap = '0.5em'
        listContainer.style.margin = 0
        listContainer.style.padding = 0
        listContainer.style.maxWidth = '100%'

        legendContainer.appendChild(listContainer)
    }

    return listContainer
}

const htmlLegendPlugin = {
    id: 'htmlLegend',
    afterUpdate(chart, args, options) {
        const ul = getOrCreateLegendList(chart, options.containerID)

        // Remove old legend items
        while (ul.firstChild) {
            ul.firstChild.remove()
        }

        // Reuse the built-in legendItems generator
        const items = chart.options.plugins.legend.labels.generateLabels(chart)

        items.forEach(item => {
            const li = document.createElement('li')
            li.classList.add('diagram-list-item')
            li.style.cursor = 'pointer'
            li.style.display = 'flex'
            li.style.flexDirection = 'row'
            li.style.marginLeft = '10px'
            li.style.hyphens = 'auto'

            li.onclick = () => {
                const { type } = chart.config
                if (type === 'pie' || type === 'doughnut') {
                    // Pie and doughnut charts only have a single dataset and visibility is per item
                    chart.toggleDataVisibility(item.index)
                } else {
                    chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))
                }
                chart.update()
            }

            // Color box
            const boxSpan = document.createElement('span')
            boxSpan.style.background = item.fillStyle
            boxSpan.style.borderColor = item.strokeStyle
            boxSpan.style.borderWidth = item.lineWidth + 'px'
            boxSpan.style.display = 'inline-block'
            boxSpan.style.flexShrink = 0

            // Text
            const textContainer = document.createElement('p')
            textContainer.style.color = item.fontColor
            textContainer.style.margin = 0
            textContainer.style.padding = 0
            textContainer.style.wordBreak = 'break-word'
            textContainer.style.textDecoration = item.hidden ? 'line-through' : ''

            const text = document.createTextNode(item.text)
            textContainer.appendChild(text)

            li.appendChild(boxSpan)
            li.appendChild(textContainer)
            ul.appendChild(li)
        })
    }
}

class DoughnutDiagram {
    /**
     * @param root {Element|HTMLElement}
     */
    constructor(root) {
        this.root = root
        this.type = this.root.dataset.diagramType
        this.data = JSON.parse(this.root.dataset.diagramData)
        this.unit = this.root.dataset.diagramData.unit
        this.init()
    }

    init() {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // eslint-disable-next-line no-new
                    const chart = new Chart(this.root.getContext('2d'), {
                        type: this.type,
                        data: this.data,
                        options: {
                            maintainAspectRatio: false,
                            responsive: true,
                            layout: {
                                padding: 10,
                                width: 225
                            },
                            plugins: {
                                htmlLegend: {
                                    containerID: this.data.legendContainer
                                },
                                legend: {
                                    display: false
                                },
                                datalabels: {
                                    display: true,
                                    color: '#fff',
                                    font: {
                                        family: 'Milo Pro',
                                        size: '16px'
                                    },
                                    formatter: function (value, context) {
                                        // add optional unit to values
                                        const unit = context.chart.data.datasets[0].unit

                                        if (value < 7.5) {
                                            return ''
                                        }

                                        if (unit) {
                                            return value + unit
                                        }
                                        return value
                                    }
                                }
                            }
                        },
                        plugins: [htmlLegendPlugin, ChartDataLabels]
                    })

                    chart.canvas.style.width = '250px'
                    chart.canvas.style.height = '250px'
                    observer.unobserve(this.root)
                }
            })
        })
        observer.observe(this.root)
    }
}

class BarDiagram {
    /**
     * @param root {Element|HTMLElement}
     */
    constructor(root) {
        this.root = root
        this.type = this.root.dataset.diagramType
        this.data = JSON.parse(this.root.dataset.diagramData)
        this.init()
    }

    init() {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // eslint-disable-next-line no-new
                    const chart = new Chart(this.root.getContext('2d'), {
                        type: 'bar',
                        data: this.data,
                        options: {
                            responsive: true,
                            interaction: {
                                intersect: false
                            },
                            animation: {
                                duration: 1500
                            },
                            scales: {
                                x: {
                                    grid: {
                                        display: false
                                    },
                                    ticks: {
                                        font: {
                                            family: 'Milo Pro',
                                            size: '18px'
                                        }
                                    },
                                    stacked: true
                                },
                                y: {
                                    ticks: {
                                        font: {
                                            family: 'Milo Pro',
                                            size: '18px'
                                        }
                                    },
                                    stacked: true
                                }
                            },
                            plugins: {
                                legend: {
                                    display: false
                                },
                                htmlLegend: {
                                    containerID: this.data.legendContainer
                                }
                            }

                        },
                        plugins: [htmlLegendPlugin]
                    })

                    chart.canvas.style.height = '500px'
                    chart.canvas.style.width = '100%'
                    observer.unobserve(this.root)
                }
            })
        })
        observer.observe(this.root)
    }
}

export { DoughnutDiagram, BarDiagram }
